import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import { useToast } from './Toast';
import { Button, Card, IconCircle, Tag } from './ui';
import { deleteMaintenanceLog, getHouseFileBlob, houseFileUrl, logMaintenance } from '@/lib/api';
import type { DueItem, HouseFile, MaintenanceLogEntry } from '@/lib/api';
import { mediumDate, todayISO } from '@/lib/dates';
import { deviceGroup, deviceRoute, dueWhen, formatBytes, intervalText, pageRefRoute } from '@/lib/house';
import { MAC_APP } from '@/lib/mac';
import { displayName } from '@/lib/people';

// Shared pieces of the Home, Dogs and Car sections and Today's "Due
// soon" card: due rows, the manuals list, the Mac file viewer, and the
// empty/forbidden states. Nothing here imports the Markdown renderer, so
// Today stays free of marked and DOMPurify.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function DueBadge({ item }: { item: DueItem }) {
  if (!item.nextDue) return <IconCircle icon="calendar" />;
  const cls =
    item.status === 'overdue'
      ? 'date-badge is-overdue'
      : item.status === 'due-soon'
        ? 'date-badge is-due-soon'
        : 'date-badge';
  return (
    <span className={cls} aria-hidden="true">
      <span className="date-badge-mon">{MONTHS[Number(item.nextDue.slice(5, 7)) - 1]}</span>
      <span className="date-badge-day">{Number(item.nextDue.slice(8, 10))}</span>
    </span>
  );
}

function WhenTag({ item, today }: { item: DueItem; today: string }) {
  const when = dueWhen(item, today);
  if (!when) return null;
  return <Tag tone={item.status === 'overdue' ? 'owe' : 'warn'}>{when}</Tag>;
}

function nextDueText(item: DueItem): string | null {
  if (!item.nextDue) return null;
  return item.status === 'overdue' ? `was due ${mediumDate(item.nextDue)}` : `due ${mediumDate(item.nextDue)}`;
}

/** Today's card and the Dogs problems: the whole row opens the page
 *  that explains the item, or its device's page when it has no page. */
export function DueLinkRow({ item, today }: { item: DueItem; today: string }) {
  const to = pageRefRoute(item.page) ?? (item.device ? deviceRoute(item.device) : null);
  const body = (
    <>
      <DueBadge item={item} />
      <span className="row-body">
        <span className="row-title is-wrap">{item.task}</span>
        <span className="row-meta is-wrap">{[item.subject, nextDueText(item)].filter(Boolean).join(' · ')}</span>
      </span>
      <span className="row-end">
        <WhenTag item={item} today={today} />
      </span>
      {to && <Icon name="chevronRight" size={18} className="row-chevron" />}
    </>
  );
  return to ? (
    <Link to={to} className="row due-link">
      {body}
    </Link>
  ) : (
    <div className="row due-link">{body}</div>
  );
}

/** Today: overdue and due within 30 days, all areas. The caller only
 *  renders it when there's something in it. */
export function DueSoonCard({ items }: { items: DueItem[] }) {
  const today = todayISO();
  return (
    <Card>
      <div className="card-head">
        <h2 className="card-title">Due soon</h2>
      </div>
      {items.map((item) => (
        <DueLinkRow key={item.id} item={item} today={today} />
      ))}
    </Card>
  );
}

/** Home's and the car's maintenance: interval, last done, next due and
 *  a Mark done button (which logs today). The subject links to the
 *  item's device page when it has a device and `linkDevice` is on. */
export function DueActionRow({
  item,
  today,
  busy,
  logged,
  onMarkDone,
  onUndo,
  linkDevice = true,
}: {
  item: DueItem;
  today: string;
  busy: boolean;
  /** This tab's own log entry for the item, which it may take back. */
  logged: MaintenanceLogEntry | null;
  onMarkDone: () => void;
  onUndo: () => void;
  linkDevice?: boolean;
}) {
  const to = pageRefRoute(item.page);
  const interval = intervalText(item).join(' · ');
  const subject =
    linkDevice && item.device ? (
      <Link to={deviceRoute(item.device)} className="due-device">
        {item.subject}
      </Link>
    ) : (
      item.subject
    );
  const history = [
    item.lastDone
      ? `last done ${mediumDate(item.lastDone.on)} by ${displayName(item.lastDone.by)}`
      : (item.everyMonths || item.everyDays) && !item.dueDate
        ? 'never logged'
        : null,
    nextDueText(item),
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className="row is-top due-row">
      <DueBadge item={item} />
      <span className="row-body">
        {to ? (
          <Link to={to} className="row-title is-wrap due-title">
            {item.task}
          </Link>
        ) : (
          <span className="row-title is-wrap">{item.task}</span>
        )}
        <span className="row-meta is-wrap">
          {subject}
          {interval && ` · ${interval}`}
        </span>
        {history && <span className="row-meta is-wrap">{history.charAt(0).toUpperCase() + history.slice(1)}</span>}
        {item.notes && <span className="row-note">{item.notes}</span>}
      </span>
      <span className="row-end">
        <WhenTag item={item} today={today} />
        {logged ? (
          <Button variant="ghost" size="sm" onClick={onUndo} disabled={busy}>
            Undo
          </Button>
        ) : (
          <Button variant="secondary" size="sm" icon="check" onClick={onMarkDone} disabled={busy}>
            {busy ? 'Saving…' : 'Mark done'}
          </Button>
        )}
      </span>
    </div>
  );
}

/** Mark done / Undo for a list of due rows. `reload` refetches /due. */
export function useMarkDone(reload: () => void) {
  const toast = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [logged, setLogged] = useState<Record<string, MaintenanceLogEntry>>({});
  const [error, setError] = useState<string | null>(null);

  const markDone = async (item: DueItem) => {
    setBusyId(item.id);
    setError(null);
    try {
      const today = todayISO();
      const entry = await logMaintenance({ taskId: item.id, on: today }, today);
      setLogged((m) => ({ ...m, [item.id]: entry }));
      toast(`Marked done: ${item.task}`);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t save.');
    } finally {
      setBusyId(null);
    }
  };

  const undo = async (item: DueItem) => {
    const entry = logged[item.id];
    if (!entry) return;
    setBusyId(item.id);
    setError(null);
    try {
      await deleteMaintenanceLog(entry.id);
      setLogged((m) => {
        const next = { ...m };
        delete next[item.id];
        return next;
      });
      toast('Removed from the log');
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t save.');
    } finally {
      setBusyId(null);
    }
  };

  return { busyId, logged, error, markDone, undo };
}

/** A section's due items with Mark done, in one card. Rows keep the
 *  order they first loaded in: a row that moved after Mark done would
 *  put a different row's button under a second click. `linkDevice` off
 *  on a device's own page. */
export function MaintenanceList({
  items,
  reload,
  linkDevice = true,
}: {
  items: DueItem[];
  reload: () => void;
  linkDevice?: boolean;
}) {
  const today = todayISO();
  const { busyId, logged, error, markDone, undo } = useMarkDone(reload);
  const order = useRef<string[]>([]);
  for (const i of items) if (!order.current.includes(i.id)) order.current.push(i.id);
  const rows = [...items].sort((a, b) => order.current.indexOf(a.id) - order.current.indexOf(b.id));
  return (
    <>
      <Card>
        {rows.map((item) => (
          <DueActionRow
            key={item.id}
            item={item}
            today={today}
            busy={busyId === item.id}
            logged={logged[item.id] ?? null}
            onMarkDone={() => void markDone(item)}
            onUndo={() => void undo(item)}
            linkDevice={linkDevice}
          />
        ))}
      </Card>
      {error && (
        <div className="error-banner" role="alert">
          {error}
        </div>
      )}
    </>
  );
}

// ─── files ───────────────────────────────────────────────────────────

/** Opens a published file. In a browser that's a plain new-tab link; the
 *  Mac window can't navigate to /api/ (its auth header rides only on
 *  fetches), so there `open` reads the bytes through api.ts and shows
 *  them in `viewer`, an in-page overlay. `open` is null in a browser. */
export function useFileViewer(): {
  open: ((path: string, name: string) => void) | null;
  viewer: ReactNode;
} {
  const [file, setFile] = useState<{ path: string; name: string } | null>(null);
  const open = useCallback((path: string, name: string) => setFile({ path, name }), []);
  const close = useCallback(() => setFile(null), []);
  return {
    open: MAC_APP ? open : null,
    viewer: file ? <FileViewer path={file.path} name={file.name} onClose={close} /> : null,
  };
}

function FileViewer({ path, name, onClose }: { path: string; name: string; onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    getHouseFileBlob(path)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Couldn’t open the file.');
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="viewer-backdrop" role="dialog" aria-modal="true" aria-label={name}>
      <div className="viewer">
        <div className="viewer-head">
          <span className="viewer-title">{name}</span>
          <button type="button" className="icon-btn is-plain" onClick={onClose} aria-label="Close" autoFocus>
            <Icon name="close" size={20} />
          </button>
        </div>
        {error ? (
          <div className="error-banner viewer-error" role="alert">
            {error}
          </div>
        ) : url ? (
          <iframe className="viewer-frame" src={url} title={name} />
        ) : (
          <p className="loading">Loading…</p>
        )}
      </div>
    </div>
  );
}

function FileRow({ file, onOpen }: { file: HouseFile; onOpen: ((path: string, name: string) => void) | null }) {
  const click = onOpen
    ? (e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        onOpen(file.path, file.name);
      }
    : undefined;
  return (
    <a className="row" href={houseFileUrl(file.path)} target="_blank" rel="noopener" onClick={click}>
      <IconCircle icon="file" />
      <span className="row-body">
        <span className="row-title is-wrap">{file.document || file.name}</span>
        <span className="row-meta">{[file.name, formatBytes(file.bytes)].filter(Boolean).join(' · ')}</span>
      </span>
      <Icon name={onOpen ? 'chevronRight' : 'external'} size={16} className="row-chevron" />
    </a>
  );
}

/** Files grouped by device, groups in the order the Worker lists them.
 *  A group whose manuals name a registry device heads with a link to its
 *  page (off with `linkDevices`, on the device's own page). */
export function FileGroups({
  files,
  onOpen,
  linkDevices = true,
}: {
  files: HouseFile[];
  onOpen: ((path: string, name: string) => void) | null;
  linkDevices?: boolean;
}) {
  const groups = new Map<string, HouseFile[]>();
  for (const f of files) {
    const key = deviceGroup(f.device);
    groups.set(key, [...(groups.get(key) ?? []), f]);
  }
  const single = groups.size === 1;
  return (
    <Card>
      {Array.from(groups, ([device, list]) => {
        const deviceId = linkDevices ? list.find((f) => f.deviceId)?.deviceId : null;
        return (
          <div key={device} className="file-group">
            {deviceId ? (
              <h3 className="file-group-head">
                <Link to={deviceRoute(deviceId)} className="file-group-link">
                  {device}
                  <Icon name="chevronRight" size={14} stroke={2} />
                </Link>
              </h3>
            ) : (
              !single && <h3 className="file-group-head">{device}</h3>
            )}
            {list.map((f) => (
              <FileRow key={f.path} file={f} onOpen={onOpen} />
            ))}
          </div>
        );
      })}
    </Card>
  );
}

// ─── states ──────────────────────────────────────────────────────────

export function NothingPublished({ icon, body }: { icon: string; body: string }) {
  return (
    <Card>
      <div className="empty">
        <IconCircle icon={icon} tone="accent" iconSize={22} />
        <h2 className="empty-title">Nothing published yet</h2>
        <p className="empty-body">{body}</p>
      </div>
    </Card>
  );
}

export function NotAMember() {
  return (
    <Card>
      <div className="empty">
        <IconCircle icon="account" tone="accent" iconSize={22} />
        <h2 className="empty-title">Not shared with you</h2>
        <p className="empty-body">
          The house pages are shared with the members of one household, and this account isn’t
          one of them.
        </p>
      </div>
    </Card>
  );
}

/** "Updated Sep 25, 2026" from an ISO timestamp or date. */
export function updatedText(iso: string | null | undefined): string | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return null;
  return `Updated ${mediumDate(iso.slice(0, 10))}`;
}
